import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { BOOK_MODELS } from "@/lib/books";
import { Loader2, BookOpenText } from "lucide-react";

/**
 * Modal launched by the "Create book" button in the CourseEditor.
 *
 * Asks for model + extra instructions + auto-share toggle, then calls
 * `onSubmit({ model, extra_instructions, auto_share, regenerate_existing })`.
 * The parent owns the actual API call so it can patch optimistic state.
 */
export default function CreateBookModal({
  open,
  onOpenChange,
  chaptersCount,
  defaultModel = "gpt-4o-mini",
  defaultAutoShare = true,
  onSubmit,
  submitting = false,
}) {
  const [model, setModel] = useState(defaultModel);
  const [extra, setExtra] = useState("");
  const [autoShare, setAutoShare] = useState(defaultAutoShare);
  const [regenerate, setRegenerate] = useState(false);

  const submit = async () => {
    await onSubmit({
      model,
      extra_instructions: extra,
      auto_share: autoShare,
      regenerate_existing: regenerate,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-book-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpenText className="h-5 w-5 text-amber-600" /> Create chapter book
          </DialogTitle>
          <DialogDescription>
            Generate a reference book for all <b>{chaptersCount}</b> chapters in
            this course. Each chapter gets a Theory, Practical, Teacher
            Delivery Notes and Student Notes section — drafted in parallel by
            your chosen model and reflecting the latest 2025–2026 best
            practices.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="book-model">Model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger id="book-model" data-testid="book-model-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BOOK_MODELS.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`book-model-${m.id}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="book-extra">Extra instructions (optional)</Label>
            <Textarea
              id="book-extra"
              data-testid="book-extra-instructions"
              placeholder="e.g. emphasise Indian curriculum context, link to recent NCERT exemplars…"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              rows={3}
            />
          </div>

          <label className="flex items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2">
            <span className="text-sm">
              <span className="font-medium">Auto-share with students</span>
              <br />
              <span className="text-stone-500 text-xs">
                Paid/partial students see each chapter's book as soon as it's
                generated. You can toggle per chapter later.
              </span>
            </span>
            <Switch
              data-testid="book-auto-share"
              checked={autoShare}
              onCheckedChange={setAutoShare}
            />
          </label>

          <label className="flex items-center justify-between gap-3 rounded-md border border-stone-200 px-3 py-2">
            <span className="text-sm">
              <span className="font-medium">Regenerate existing</span>
              <br />
              <span className="text-stone-500 text-xs">
                Overwrite any books already drafted for this course.
              </span>
            </span>
            <Switch
              data-testid="book-regenerate"
              checked={regenerate}
              onCheckedChange={setRegenerate}
            />
          </label>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="book-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="book-start"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…
              </>
            ) : (
              <>Start generation</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
